import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  PersonAdd as PersonAddIcon
} from '@mui/icons-material';
import PageHeader from '../common/PageHeader';
import Loading from '../common/Loading';
import {
  createOperator,
  createOperatorSubAccount,
  fetchOperatorUsers,
  fetchOperators,
  setOperatorSubAccountActive,
  updateOperator
} from '../../redux/slices/operatorsSlice';
import { selectCanManageOperators } from '../../redux/slices/authSlice';

const emptyOperatorForm = {
  id: '',
  name: '',
  legalName: '',
  contactName: '',
  email: '',
  phone: '',
  address: '',
  vehicleLimit: 5,
  driverLimit: 10,
  notes: '',
  active: true
};

const emptySubAccountForm = {
  operatorId: '',
  name: '',
  email: '',
  phone: '',
  password: '',
  role: 'operator_admin'
};

const OperatorsPage = () => {
  const dispatch = useDispatch();
  const canManageOperators = useSelector(selectCanManageOperators);
  const { operators, usersByOperatorId, isLoading, isSaving, error } = useSelector(state => state.operators);

  const [operatorDialogOpen, setOperatorDialogOpen] = useState(false);
  const [operatorForm, setOperatorForm] = useState(emptyOperatorForm);
  const [subAccountDialogOpen, setSubAccountDialogOpen] = useState(false);
  const [subAccountForm, setSubAccountForm] = useState(emptySubAccountForm);

  useEffect(() => {
    dispatch(fetchOperators());
  }, [dispatch]);

  useEffect(() => {
    operators.forEach(operator => {
      dispatch(fetchOperatorUsers(operator.id));
    });
  }, [dispatch, operators]);

  const operatorCount = operators.length;
  const activeOperatorCount = useMemo(
    () => operators.filter(operator => operator.active !== false).length,
    [operators]
  );

  const openNewOperatorDialog = () => {
    setOperatorForm(emptyOperatorForm);
    setOperatorDialogOpen(true);
  };

  const openEditOperatorDialog = (operator) => {
    setOperatorForm({
      id: operator.id,
      name: operator.name || '',
      legalName: operator.legalName || '',
      contactName: operator.contactName || '',
      email: operator.email || '',
      phone: operator.phone || '',
      address: operator.address || '',
      vehicleLimit: operator.vehicleLimit || 5,
      driverLimit: operator.driverLimit || 10,
      notes: operator.notes || '',
      active: operator.active !== false
    });
    setOperatorDialogOpen(true);
  };

  const openSubAccountDialog = (operatorId = '') => {
    setSubAccountForm({ ...emptySubAccountForm, operatorId });
    setSubAccountDialogOpen(true);
  };

  const handleOperatorSubmit = async () => {
    if (!operatorForm.name.trim()) return;

    const action = operatorForm.id
      ? updateOperator(operatorForm)
      : createOperator(operatorForm);
    const result = await dispatch(action);

    if (!result.error) {
      setOperatorDialogOpen(false);
      dispatch(fetchOperators());
    }
  };

  const handleSubAccountSubmit = async () => {
    if (!subAccountForm.operatorId || !subAccountForm.name.trim() || !subAccountForm.email.trim() || !subAccountForm.password) {
      return;
    }

    const result = await dispatch(createOperatorSubAccount(subAccountForm));
    if (!result.error) {
      setSubAccountDialogOpen(false);
      dispatch(fetchOperators());
    }
  };

  const toggleSubAccountActive = (operatorId, user) => {
    dispatch(setOperatorSubAccountActive({
      userId: user.id,
      operatorId,
      active: user.active === false
    }));
  };

  if (!canManageOperators) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">
          Sie haben keine Berechtigung fuer die Unternehmer- und Sub-Konten-Verwaltung.
        </Alert>
      </Box>
    );
  }

  if (isLoading && operators.length === 0) {
    return <Loading message="Unternehmer werden geladen..." />;
  }

  return (
    <Box sx={{ flexGrow: 1, p: 3 }}>
      <PageHeader
        title="Unternehmer & Sub-Konten"
        description="Unternehmer, Kontingente und Portal-Zugaenge verwalten"
        breadcrumbs={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'Profil', path: '/profil' },
          { label: 'Unternehmer', path: '/profil/unternehmer' }
        ]}
        actionButton={{
          label: 'Unternehmer anlegen',
          icon: <AddIcon />,
          onClick: openNewOperatorDialog
        }}
        secondaryButton={{
          label: 'Sub-Konto anlegen',
          icon: <PersonAddIcon />,
          onClick: () => openSubAccountDialog()
        }}
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
      )}

      <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
        <Chip label={`${activeOperatorCount}/${operatorCount} aktiv`} color="primary" />
        <Chip label="Sub-Konten nur fuer Portalzugriff" variant="outlined" />
      </Stack>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Unternehmer</TableCell>
              <TableCell>Kontakt</TableCell>
              <TableCell align="center">Fahrzeuge</TableCell>
              <TableCell align="center">Fahrer</TableCell>
              <TableCell>Sub-Konten</TableCell>
              <TableCell align="right">Aktionen</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {operators.map(operator => {
              const users = usersByOperatorId[operator.id] || [];
              return (
                <TableRow key={operator.id}>
                  <TableCell>
                    <Typography variant="subtitle2">{operator.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {operator.legalName || operator.id}
                    </Typography>
                    <Box sx={{ mt: 0.5 }}>
                      <Chip
                        size="small"
                        label={operator.active === false ? 'Inaktiv' : 'Aktiv'}
                        color={operator.active === false ? 'default' : 'success'}
                      />
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{operator.contactName || '-'}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {operator.email || operator.phone || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">{operator.vehicleLimit || 5}</TableCell>
                  <TableCell align="center">{operator.driverLimit || 10}</TableCell>
                  <TableCell>
                    {users.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">Keine Sub-Konten</Typography>
                    ) : (
                      <Stack spacing={0.75}>
                        {users.map(user => (
                          <Stack key={user.id} direction="row" spacing={1} alignItems="center">
                            <Chip
                              size="small"
                              label={user.active === false ? 'Inaktiv' : 'Aktiv'}
                              color={user.active === false ? 'default' : 'success'}
                            />
                            <Typography variant="body2">{user.name || user.email}</Typography>
                            <Button size="small" onClick={() => toggleSubAccountActive(operator.id, user)}>
                              {user.active === false ? 'Aktivieren' : 'Deaktivieren'}
                            </Button>
                          </Stack>
                        ))}
                      </Stack>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      <Button size="small" startIcon={<EditIcon />} onClick={() => openEditOperatorDialog(operator)}>
                        Bearbeiten
                      </Button>
                      <Button size="small" startIcon={<PersonAddIcon />} onClick={() => openSubAccountDialog(operator.id)}>
                        Sub-Konto
                      </Button>
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })}
            {operators.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography color="text.secondary">Noch keine Unternehmer angelegt.</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={operatorDialogOpen} onClose={() => setOperatorDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{operatorForm.id ? 'Unternehmer bearbeiten' : 'Unternehmer anlegen'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2.25} sx={{ mt: 1 }}>
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Stammdaten
              </Typography>
              <Stack spacing={2}>
                <TextField
                  fullWidth
                  required
                  label="Anzeigename"
                  value={operatorForm.name}
                  onChange={(e) => setOperatorForm({ ...operatorForm, name: e.target.value })}
                  helperText="Dieser Name wird in Listen, Zuordnungen und Sub-Konten angezeigt."
                />
                <TextField
                  fullWidth
                  label="Rechtlicher Name"
                  value={operatorForm.legalName}
                  onChange={(e) => setOperatorForm({ ...operatorForm, legalName: e.target.value })}
                  placeholder="Optional, z. B. vollständige Firmierung"
                />
              </Stack>
            </Box>

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Kontakt
              </Typography>
              <Stack spacing={2}>
                <TextField
                  fullWidth
                  label="Ansprechpartner"
                  value={operatorForm.contactName}
                  onChange={(e) => setOperatorForm({ ...operatorForm, contactName: e.target.value })}
                />
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      type="email"
                      label="E-Mail"
                      value={operatorForm.email}
                      onChange={(e) => setOperatorForm({ ...operatorForm, email: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Telefon"
                      value={operatorForm.phone}
                      onChange={(e) => setOperatorForm({ ...operatorForm, phone: e.target.value })}
                    />
                  </Grid>
                </Grid>
                <TextField
                  fullWidth
                  label="Adresse"
                  value={operatorForm.address}
                  onChange={(e) => setOperatorForm({ ...operatorForm, address: e.target.value })}
                />
              </Stack>
            </Box>

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Kontingente
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Fahrzeuglimit"
                    value={operatorForm.vehicleLimit}
                    onChange={(e) => setOperatorForm({ ...operatorForm, vehicleLimit: e.target.value })}
                    inputProps={{ min: 0 }}
                    helperText="Maximal anlegbare Fahrzeuge"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Fahrerlimit"
                    value={operatorForm.driverLimit}
                    onChange={(e) => setOperatorForm({ ...operatorForm, driverLimit: e.target.value })}
                    inputProps={{ min: 0 }}
                    helperText="Maximal anlegbare Fahrer"
                  />
                </Grid>
              </Grid>
            </Box>

            <TextField
              fullWidth
              multiline
              minRows={2}
              label="Notizen"
              value={operatorForm.notes}
              onChange={(e) => setOperatorForm({ ...operatorForm, notes: e.target.value })}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={operatorForm.active}
                  onChange={(e) => setOperatorForm({ ...operatorForm, active: e.target.checked })}
                />
              }
              label="Unternehmer ist aktiv"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOperatorDialogOpen(false)}>Abbrechen</Button>
          <Button variant="contained" disabled={isSaving || !operatorForm.name.trim()} onClick={handleOperatorSubmit}>
            Speichern
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={subAccountDialogOpen} onClose={() => setSubAccountDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Sub-Konto anlegen</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <FormControl fullWidth required>
              <InputLabel id="operator-label">Unternehmer</InputLabel>
              <Select
                labelId="operator-label"
                label="Unternehmer"
                value={subAccountForm.operatorId}
                onChange={(e) => setSubAccountForm({ ...subAccountForm, operatorId: e.target.value })}
              >
                {operators.map(operator => (
                  <MenuItem key={operator.id} value={operator.id}>{operator.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField required label="Name" value={subAccountForm.name} onChange={(e) => setSubAccountForm({ ...subAccountForm, name: e.target.value })} />
            <TextField required label="E-Mail" value={subAccountForm.email} onChange={(e) => setSubAccountForm({ ...subAccountForm, email: e.target.value })} />
            <TextField label="Telefon" value={subAccountForm.phone} onChange={(e) => setSubAccountForm({ ...subAccountForm, phone: e.target.value })} />
            <TextField required type="password" label="Initiales Passwort" value={subAccountForm.password} onChange={(e) => setSubAccountForm({ ...subAccountForm, password: e.target.value })} helperText="Mindestens 8 Zeichen. Passwort spaeter per Firebase Auth zuruecksetzen lassen." />
            <FormControl fullWidth>
              <InputLabel id="role-label">Rolle</InputLabel>
              <Select
                labelId="role-label"
                label="Rolle"
                value={subAccountForm.role}
                onChange={(e) => setSubAccountForm({ ...subAccountForm, role: e.target.value })}
              >
                <MenuItem value="operator_admin">Unternehmer-Admin</MenuItem>
                <MenuItem value="operator_staff">Unternehmer-Mitarbeiter</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSubAccountDialogOpen(false)}>Abbrechen</Button>
          <Button
            variant="contained"
            disabled={isSaving || !subAccountForm.operatorId || !subAccountForm.name || !subAccountForm.email || subAccountForm.password.length < 8}
            onClick={handleSubAccountSubmit}
          >
            Anlegen
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OperatorsPage;
